import { languages } from '@app/configurations';
import { Currency } from '@app/core/models/customer.models';
import { AllMetrics } from '@app/core/models/metrics.constants';
import { UserLanguage } from '@app/core/models/user.models';
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  getMetricSummation,
  isGermanLocale,
  RemoveDelaySuffix,
} from '@app/core/services/metrics-calculations.service';
import { months, shortMonths, weekdays } from '@app/core/services/utilities.service';
import {
  GetDateWithoutTimeZone,
  IsCurrentDayinlast3Days,
  isFirstDayOfQuarter,
  isLastDayOfQuarter,
  LocalizedDate,
  LocalizedMonthName,
} from '@app/core/utilities/date.utilities';
import { GetMetricTypeInfo, getTransformedRefinerKey, MetricTypeInfo } from '@app/core/utilities/refiners.utilities';
import {
  addDays,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarQuarters,
  differenceInCalendarWeeks,
  differenceInDays,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  format,
  getQuarter,
  isAfter,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isWithinInterval,
  min,
  startOfDay,
} from 'date-fns';
import {
  AxisValues,
  CHART_COLORS,
  ChartData,
  ChartFrequency,
  ChartMarkers,
  ChartMetricConfig,
  DefaultMetrics,
  FrequencyOptions,
  MAX_CHART_LINES,
} from './chart.models';

export function SetHighChartsLocaleOptions(lang: UserLanguage) {
  const options: any = {
    chart: { style: { fontFamily: 'proxima-nova, sans-serif' } },
  };

  switch (lang) {
    case languages.ENGLISH:
      options.lang = { thousandsSep: ',' };
      break;
    case languages.GERMAN:
      options.lang = {
        decimalPoint: ',',
        thousandsSep: '.',
        loading: 'Daten werden geladen...',
        months: months[languages.GERMAN],
        weekdays: weekdays[languages.GERMAN],
        shortMonths: shortMonths[languages.GERMAN],
        exportButtonTitle: 'Exportieren',
        printButtonTitle: 'Drucken',
        rangeSelectorFrom: 'Von',
        rangeSelectorTo: 'Bis',
        rangeSelectorZoom: 'Zeitraum',
        downloadPNG: 'Download als PNG-Bild',
        downloadJPEG: 'Download als JPEG-Bild',
        downloadPDF: 'Download als PDF-Dokument',
        downloadSVG: 'Download als SVG-Bild',
        resetZoom: 'Zoom zurücksetzen',
        resetZoomTitle: 'Zoom zurücksetzen',
      };
      break;
    case languages.CHINESE:
      options.lang = {
        contextButtonTitle: '图表导出菜单',
        decimalPoint: '.',
        thousandsSep: ',',
        downloadJPEG: '下载 JPEG 图片',
        downloadPDF: '下载 PDF 文件',
        downloadPNG: '下载 PNG 文件',
        downloadSVG: '下载 SVG 文件',
        downloadXLS: '下载 XLS 文件',
        drillUpText: '返回 {series.name}',
        invalidDate: '无效的时间',
        loading: '加载中...',
        months: months[languages.CHINESE],
        noData: '没有数据',
        numericSymbols: null,
        printChart: '打印图表',
        resetZoom: '重置缩放比例',
        resetZoomTitle: '重置为原始大小',
        shortMonths: shortMonths[languages.CHINESE],
        weekdays: weekdays[languages.CHINESE],
        viewData: '查看数据表格',
      };
      break;
  }
  return options;
}

export function ChartValueFormatters(
  value: string | number,
  metric: AllMetrics,
  currency?: Currency,
  delay: boolean = false,
  lessThan7: boolean = false,
  decimalDigits = 2,
) {
  const { type } = GetMetricTypeInfo(metric, delay, lessThan7);
  if (type === 'currency') value = formatCurrency(value, currency, false, decimalDigits);
  else if (type === 'percent') value = formatPercent(value, false, decimalDigits);
  else if (type === 'number') value = formatNumber(value, false, decimalDigits);

  if (value?.toString()?.endsWith(`${isGermanLocale() ? ',' : '.'}00`)) {
    value = value.toString().slice(0, -3);
  }

  return value.toString();
}

export const IsFirstVisibleDayofMonth = (positions, dates, curr) => {
  const visibleDaysOfMonth = positions
    .map((d) => new Date(dates[d]))
    .filter((d) => d?.getMonth() === curr.getMonth() && d.getYear() === curr.getYear());

  return !visibleDaysOfMonth.find((d) => +d < +curr);
};

export function GetChartDefaults(dates): { defaultFrequency: ChartFrequency; defaultMetrics: AllMetrics[] } {
  let frequency = ChartFrequency.DAILY;
  const daysCount = dates?.length ? differenceInCalendarDays(dates[1], dates[0]) : null;
  if (daysCount > 30) frequency = ChartFrequency.WEEKLY;
  else if (daysCount > 90) frequency = ChartFrequency.MONTHLY;
  else if (daysCount > 180) frequency = ChartFrequency.QUARTERLY;

  return { defaultFrequency: frequency, defaultMetrics: DefaultMetrics };
}

export function NextAvailableFrequency(current, disabledFrequencies) {
  if (!disabledFrequencies.includes(current)) return current;

  return NextAvailableFrequency(FrequencyOptions[FrequencyOptions.indexOf(current) + 1], disabledFrequencies);
}

export function VisibleMetrics(
  defaultMetrics: any = [],
  availableMetrics: MetricTypeInfo[],
  delay: boolean,
  lessThan7: boolean,
): AllMetrics[] {
  const visibleMetrics: AllMetrics[] = defaultMetrics.map((d) =>
    availableMetrics.find(({ key }) => d === RemoveDelaySuffix(key)) ? d : null,
  );
  return visibleMetrics.map((d) => <AllMetrics>getTransformedRefinerKey(d, { delay, lessThan7 }));
}

export function LabelAlignment(frequency: ChartFrequency) {
  return [ChartFrequency.DAILY, ChartFrequency.WEEKLY].includes(frequency) ? 'left' : 'center';
}

export function TicktInterval(frequency: ChartFrequency, count: number): number {
  if (count > 39 && frequency === ChartFrequency.DAILY) return 1 + Math.ceil(count / 100);
  if (count > 14 && frequency === ChartFrequency.WEEKLY) return 1 + Math.ceil(count / 21);
  if (count > 17 && frequency === ChartFrequency.MONTHLY) return 2;
  if (count > 20 && frequency === ChartFrequency.QUARTERLY) return 2;

  return 1;
}

export function DataForFrequency(
  data: ChartData[],
  key: AllMetrics,
  frequency: ChartFrequency,
  xAxisValues: AxisValues[] | Date[],
): number[] {
  const tmp = [];
  xAxisValues.forEach((value) => {
    let val;
    if (frequency === ChartFrequency.DAILY)
      val = data.find((d) => format(value.date, 'MMddyyyy') === format(GetDateWithoutTimeZone(d.date), 'MMddyyyy'))?.[
        key
      ];
    else
      val = getMetricSummation(
        data.filter((d) =>
          isWithinInterval(GetDateWithoutTimeZone(d.date), {
            start: GetDateWithoutTimeZone(value.start),
            end: GetDateWithoutTimeZone(value.end),
          }),
        ),
        key,
      );

    tmp.push(isNaN(val) ? null : val);
  });

  return tmp;
}

function getMetricPositions(metrics): AllMetrics[] {
  const positions = new Array(MAX_CHART_LINES).fill(null);
  metrics
    .filter((d) => d.enable)
    .forEach((d) => {
      positions[d.index] = d.visible ? d.id : null;
    });
  return positions;
}
function getUpdatedMetrics(metrics, positions) {
  const newMetrics = metrics.map((d) => {
    const index = positions.indexOf(d.id);
    return {
      visible: index > -1,
      enable: index > -1,
      index: index > -1 ? index : null,
      marker: index > -1 ? ChartMarkers[index] : null,
      id: d.id,
    };
  });

  positions.forEach((d, index) => {
    if (d === null) {
      const itemIndex = newMetrics.findIndex((e) => !e.enable && !e.visible);
      newMetrics[itemIndex].enable = true;
      newMetrics[itemIndex].index = index;
      newMetrics[itemIndex].marker = ChartMarkers[index];
    }
  });
  return newMetrics;
}

export function RemoveMetric(metrics: ChartMetricConfig[], id: AllMetrics) {
  const positions = getMetricPositions(metrics).map((d) => (d === id ? null : d));

  return getUpdatedMetrics(metrics, positions);
}
export function ReplaceMetric(metrics: ChartMetricConfig[], currentMetric: AllMetrics, newMetric: AllMetrics) {
  const positions = getMetricPositions(metrics).map((d) => (d === currentMetric ? newMetric : d));

  return getUpdatedMetrics(metrics, positions);
}
export function AddMetric(metrics: ChartMetricConfig[], newMetric: AllMetrics, newPosition: number) {
  const positions = getMetricPositions(metrics);
  positions[newPosition] = newMetric;

  return getUpdatedMetrics(metrics, positions);
}

export function GenerateAxisValues(
  itemCount: number,
  hasAMZaccount: boolean,
  dateRange: [Date, Date],
  formatName: (start: Date, end?: Date) => string,
  getEnd?: (start: Date) => Date,
  checkPartial: (start: Date, end?: Date) => boolean = () => false,
) {
  let lastEnd: Date;
  const xAxisValues = [];
  const addAxisValue = (start: Date, end?: Date, isPartial?: true): void => {
    const isAmzPartial = hasAMZaccount && IsCurrentDayinlast3Days([start, end || start]);

    xAxisValues.push({
      isAmzPartial: isAmzPartial,
      name: formatName(start, end),
      isPartial: isPartial || checkPartial(start, end) || isAmzPartial,
      start: format(start, 'yyyy-MM-dd'), // exclude time to check date equality
      end: end && format(end, 'yyyy-MM-dd'), // exclude time to check date equality
    });
  };

  for (let i = 0; i < itemCount; i++) {
    const start = lastEnd ? addDays(lastEnd, 1) : dateRange[0];
    const end = getEnd ? min([dateRange[1], getEnd(start)]) : void 0;

    lastEnd = end || start;
    addAxisValue(start, end);
    if (end && i + 1 === itemCount && isAfter(dateRange[1], end)) addAxisValue(addDays(lastEnd, 1), dateRange[1], true);
  }

  return xAxisValues;
}

export function GenerateWeeklyCategories([start, end], hasAMZaccount) {
  const count = Math.abs(differenceInCalendarWeeks(end, start, { weekStartsOn: 1 })) + 1;

  return GenerateAxisValues(
    count,
    hasAMZaccount,
    [start, end],
    (start, end) => [LocalizedDate(start, 'd MMM'), LocalizedDate(end, 'd MMM')].join(' - '),
    (start) => endOfWeek(start, { weekStartsOn: 1 }),
    (start, end) => differenceInCalendarDays(end, start) < 6,
  );
}

export function GenerateMonthlyCategories([start, end], hasAMZaccount) {
  const count = Math.abs(differenceInCalendarMonths(end, start)) + 1;

  return GenerateAxisValues(
    count,
    hasAMZaccount,
    [start, end],
    (start) => `${LocalizedMonthName(start, true)} ${format(start, 'yyyy')}`,
    (start) => endOfMonth(start),
    (start, end) => !isFirstDayOfMonth(start) || !isLastDayOfMonth(end),
  );
}

export function GenerateQuarterlyCategories([start, end], hasAMZaccount) {
  const count = Math.abs(differenceInCalendarQuarters(end, start)) + 1;

  return GenerateAxisValues(
    count,
    hasAMZaccount,
    [start, end],
    (start) => `Q${getQuarter(start)} ${format(start, 'yyyy')}`,
    (start) => endOfQuarter(start),
    (start, end) => !isFirstDayOfQuarter(start) || !isLastDayOfQuarter(end),
  );
}

export function GenerateDailyCategories([start, end], hasAMZaccount) {
  const data = [];
  for (let i = 0; i < differenceInDays(end, start) + 1; i++) {
    const currentDate = addDays(start, i);
    const isCurrentDayinlast3Days = IsCurrentDayinlast3Days([startOfDay(currentDate), endOfDay(currentDate)]);
    const isAmzPartial = hasAMZaccount && isCurrentDayinlast3Days;
    data.push({
      isAmzPartial,
      date: currentDate,
      isPartial: isAmzPartial,
      name: LocalizedDate(currentDate, 'dd MMM yyyy'),
    });
  }
  return data;
}

export function RenderStartEndLine(chart) {
  if (!chart.xAxis[0].ticks.length) return;
  const { x, width } = chart.xAxis[0].axisLine.renderer.plotBox;
  const { y, height } = chart.xAxis[0].ticks[0].gridLine.renderer.plotBox;
  const getStartPath = `M ${x} ${y} L ${x} ${y + height} Z`;
  const getEndPath = `M ${x + width} ${y} L ${x + width} ${y + height} Z`;
  if (chart.customStartline) chart.customStartline.destroy();
  if (chart.customEndline) chart.customEndline.destroy();
  chart.customStartline = chart.renderer
    .path([getStartPath])
    .attr({ stroke: CHART_COLORS.grindLines, 'stroke-width': 1, zIndex: 10, opacity: 1 })
    .add();
  chart.customEndline = chart.renderer
    .path([getEndPath])
    .attr({ stroke: CHART_COLORS.grindLines, 'stroke-width': 1, zIndex: 10, opacity: 1 })
    .add();
}
